"use client";

import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { useInput } from "@/hooks/use-input";
import { validateEmail } from "@/utils/validationUtils";
import showToast from "../utils/toastNotifications";
import { trackNewsletterSignup } from "@/lib/analytics";
import { LoadingOutlined } from '@ant-design/icons';

export default function NewsletterCard() {
  const {
    value: email,
    handleInputChange: handleEmailChange,
    handleInputBlur: handleEmailBlur,
    hasError: emailHasError,
    errorMessage: emailErrorMessage,
    reset: resetEmail,
  } = useInput("", validateEmail);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (emailHasError || !email) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error("Failed to subscribe");
      }

      setSubmitStatus("success");
      // The email address itself is never reported, only where they signed up.
      trackNewsletterSignup({ sourcePage: window.location.pathname });
      resetEmail();

      // Reset success message after 3 seconds
      setTimeout(() => setSubmitStatus(null), 3000);
    } catch (error) {
      console.error("Newsletter submit error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!submitStatus) return;
    if (submitStatus === "success") {
      showToast("success", "Successfully Subscribed");
    }
  }, [submitStatus]);

  return (
    <div className="flex flex-col items-center w-full max-w-[320px] lg:max-w-[360px] xl:max-w-[400px] h-[170px] md:h-[175px] xl:h-[170px] rounded-[9px] ring-[0.5px] ring-[#FC4FF6] dark:bg-[#000000] bg-[#F7F2FF] py-3.5 px-2.5">
      <h3 className="text-center font-semibold font-inter text-[15px] md:text-xs tablet:text-[15px] dark:text-[#FFFFFF] text-[#443D43] mb-2 uppercase">
        Join our newsletter
      </h3>
      <p className="text-center font-normal font-inter text-xs md:text-[10px] tablet:text-xs dark:text-[#FFFFFF] text-[#443D43] mb-4">
        Be the first to receive our latest case studies, insights and
        innovations.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3 w-full px-2 3xl:px-4">
        <div className="relative w-full">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            disabled={isSubmitting}
            className="w-full h-[35px] px-2 pr-12 text-xs rounded-[5px] border dark:border-[#FFFFFF33] border-[#78778B] dark:bg-[#181A20] bg-[#F7F2FF] dark:text-white text-[#3B3B3B] dark:placeholder:text-[#FFFFFF99] placeholder:text-[#78778B] placeholder:text-xs focus:outline-none dark:focus:border-transparent focus:border-[#78778B80]"
          />
          <button
            type="submit"
            disabled={isSubmitting || !email || emailHasError}
            className={`absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full transition-all ${
              !email
                ? "bg-[#FF1CF733] cursor-not-allowed"
                : emailHasError
                ? "bg-[#FF1CF780] cursor-not-allowed"
                : "bg-[#FF1CF7B2] hover:bg-[#FF1CF7] dark:hover:bg-[#FF1CF7] cursor-pointer"
            }`}
            aria-label="Subscribe to newsletter"
          >
            {isSubmitting ? (
              <LoadingOutlined />
            ) : (
              <ArrowRight
                className={"dark:text-[#FFFFFF99] text-[#2E2E2E]"}
                size={14}
              />
            )}
          </button>
        </div>
        {emailHasError && emailErrorMessage && (
          <p className="text-center text-xs text-[#FF0000] mt-1">
            {emailErrorMessage}
          </p>
        )}
        {submitStatus === "error" && (
          <p className="text-center text-xs text-red-500 dark:text-red-400">
            Something went wrong. Please try again.
          </p>
        )}
      </form>
    </div>
  );
}
